import cloneDeep from 'clone-deep'
import { Frequency } from 'components/Frequency'
import { validateFrequencyData } from 'components/Frequency/logic'
import { InputType } from 'config'
import { accountTypes } from 'config/user.type.constants'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { getByteproFileName } from 'services'
import { createReminder, getReminderById, updateReminder } from 'services/apis/reminder'
import { Modal } from 'stories/components'
import { getLoanDisplayNo, InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { defaultReminderInputs, reminderInitialData } from './constants'

export const ReminderDetailModal = ({
  reminderId,
  onModalOk,
  userEmails,
  onModalClose,
  loanNumber = 0,
  templateDefaults = null,
}: {
  reminderId: number
  onModalOk: Function
  userEmails: string[]
  onModalClose: () => void
  loanNumber: number
  templateDefaults?: Record<string, any> | null
}) => {
  const [loanGlobalStatus, loanDetail] = useSelector((state: any) => [state.loanGlobalStatus, state.loanDetail])

  const [isLoading, setIsLoading] = useState(false)
  const [inputs, setInputs] = useState<Record<string, InputType>>(defaultReminderInputs(loanGlobalStatus, reminderId))
  const [data, setData] = useState<Record<string, any>>(reminderInitialData())
  const [errorCheck, setErrorCheck] = useState(false)
  const isCreate = reminderId === -1

  const assignOptions = useMemo(() => {
    const options = { ...accountTypes }
    userEmails.forEach((email) => (options[email] = email))
    return options
  }, [userEmails])

  const initData = async () => {
    setIsLoading(true)
    const res = await getReminderById(reminderId)
    setData(res)
    setIsLoading(false)
  }
  useEffect(() => {
    let cancelled = false
    if (!isCreate) {
      initData()
      return
    }
    // Redux still holds the previous loan's BytePro file name until that loan's overview
    // is replaced. Prefer it only when it is this loan; otherwise look this loan up.
    const sameLoan = !!loanNumber && String(loanDetail.loanNumber) === String(loanNumber)
    setData({
      ...reminderInitialData(),
      ...(sameLoan
        ? { loanNumber: getLoanDisplayNo({ byteproFileName: loanDetail.byteproFileName, loanNumber }) }
        : {}),
      ...(templateDefaults || {}),
    })
    if (sameLoan || !loanNumber) return
    getByteproFileName(Number(loanNumber))
      .then((res: any) => {
        if (cancelled) return
        const byteproFileName = res?.success ? Number(res.data) || 0 : 0
        setData((current) => ({
          ...current,
          loanNumber: getLoanDisplayNo({ byteproFileName, loanNumber }),
        }))
      })
      .catch(() => {
        if (cancelled) return
        setData((current) => ({
          ...current,
          loanNumber: getLoanDisplayNo({ loanNumber }),
        }))
      })
    return () => {
      cancelled = true
    }
  }, [])

  const onChange = (key: string, value: any) => {
    const temp = cloneDeep(data)
    temp[key] = value
    setData(temp)
  }

  const onOk = async () => {
    let hasError = false
    const newInputs = cloneDeep(inputs)
    Object.keys(inputs).map((key) => {
      newInputs[key].error = InputValidate({ ...newInputs[key], value: data[key] })
      if (newInputs[key].error?.length) hasError = true
      data[key] = newInputs[key].value
    })
    setInputs(newInputs)

    hasError = hasError || validateFrequencyData(data.frequency)
    setErrorCheck(true)
    if (hasError) return

    let newItem
    setIsLoading(true)
    if (isCreate) {
      newItem = await createReminder(data)
      toast('Successfully created a Reminder', { type: 'success' })
    } else {
      newItem = await updateReminder(data.id, data)
      toast('Successfully updated a Reminder', { type: 'success' })
    }
    setIsLoading(false)
    onModalOk(newItem)
  }

  return (
    <Modal
      title={`${isCreate ? 'Add' : 'Edit'} Reminder${!isCreate ? ` - ${data.no}` : ''}`}
      onOk={onOk}
      onClose={onModalClose}
      isOpen
      loading={isLoading}
    >
      <div className="relative w-full max-w-[500px]">
        {Object.keys(inputs).map((key) => {
          if (key === 'completeAt' && !data['loanNumber']) return
          const input: any = inputs[key]
          input.value = data[key]
          if (key === 'assignedTo') {
            input.options = assignOptions
            input.value = data[key] || []
          }
          return (
            <>
              <div className="mb-2" key={key}>
                <RenderInput input={input} Key={key} key={key} onChange={onChange} />
              </div>
              {key === 'description' && (
                <Frequency
                  data={data.frequency}
                  startDate={data.date}
                  onChange={(value: any) => onChange('frequency', value)}
                  errorCheck={errorCheck}
                />
              )}
            </>
          )
        })}
      </div>
    </Modal>
  )
}
